import React from 'react';
import {useTranslation} from 'react-i18next';
import {animated} from 'react-spring';

import ErrorIcon from '@edna/icons/error.svg';
import InfoIcon from '@edna/icons/info.svg';
import SuccessIcon from '@edna/icons/success.svg';
import WarningIcon from '@edna/icons/warning.svg';

import {CircularProgressBar} from '../CircularProgressBar';
import * as S from './style';
import {
  DEFAULT_DURATION,
  DEFAULT_TYPE,
  ENotificationType,
  TNewNotification,
  TNotificationType,
} from './types';
import {useTimer} from './useTimer';
import {isDarkNotification} from './utils';

const iconMap: Partial<
  Record<TNotificationType, React.ComponentType<React.ComponentProps<'svg'>>>
> = {
  success: SuccessIcon,
  info: InfoIcon,
  warning: WarningIcon,
  error: ErrorIcon,

  SUCCESS: SuccessIcon,
  INFO: InfoIcon,
  WARNING: WarningIcon,
  ERROR: ErrorIcon,
};

type TProps = TNewNotification & {
  removeNotification: (id: TNewNotification['id']) => void;
  style?: TAnyObject;
  innerRef?: React.RefCallback<HTMLDivElement>;
};

const AnimatedProgressBar = animated(CircularProgressBar);

const PermanentItem = React.memo<Partial<TProps> & {children: React.ReactNode}>(
  ({type, onClick, children}) => (
    <S.Item type={type} onClick={onClick}>
      {children}
    </S.Item>
  ),
);

const ClosibleItem = React.memo<TProps & {children: React.ReactNode}>(
  ({
    id,
    closeButtonText,
    onCloseButtonClick,
    lifeDuration = DEFAULT_DURATION,
    type,
    onClick,
    children,
    onClose,
    removeNotification,
  }) => {
    const handleClose = React.useCallback(() => {
      onCloseButtonClick?.();
      onClose?.();
      removeNotification(id);
    }, [onClose, removeNotification, id, onCloseButtonClick]);
    const {lifeStyles, startTimer, removeTimer} = useTimer(lifeDuration, handleClose);

    return (
      <S.Item onMouseEnter={removeTimer} onMouseLeave={startTimer} type={type} onClick={onClick}>
        {children}
        {closeButtonText && (
          <S.NotificationButton onClick={handleClose}>{closeButtonText}</S.NotificationButton>
        )}
        {!closeButtonText && (
          <AnimatedProgressBar
            percentage={lifeStyles.percentage}
            size={32}
            color={isDarkNotification(type) ? 'grey900' : 'white'}
          >
            <S.CloseIcon
              role="button"
              title="close"
              onClick={handleClose}
              size={lifeDuration === 'infinity' ? 'L' : 'M'}
            />
          </AnimatedProgressBar>
        )}
      </S.Item>
    );
  },
);

const Item: React.FC<TProps> = ({
  title,
  message,
  messageKey,
  messageArgs,
  type = DEFAULT_TYPE,
  isPermanent,
  style,
  innerRef,
  ...props
}) => {
  const {t} = useTranslation();

  const Icon = iconMap[type];

  const ItemComponent = isPermanent ? PermanentItem : ClosibleItem;
  const messageContent = messageKey ? t(messageKey, messageArgs) : message;

  return (
    <animated.div style={style}>
      <S.ItemWrapper ref={innerRef}>
        <ItemComponent {...props} type={type}>
          <S.Content>
            {Icon && <S.Icon as={Icon} />}
            <S.Message>
              {title && (
                <>
                  <S.Title>{title}</S.Title>
                  <div>{messageContent}</div>
                </>
              )}
              {!title && messageContent}
            </S.Message>
          </S.Content>
        </ItemComponent>
      </S.ItemWrapper>
    </animated.div>
  );
};

export {ENotificationType};
export type {TProps};

export {Item};
